import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { BootstrapDialog, Header } from './styles';
import {
  FieldArray, FormikProvider, useFormik,
} from 'formik';
import InputGroup from './InputGroup';
import { formatValueInBrazilianCurrency } from 'utils';
import * as Yup from 'yup';

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{ m: 0, p: 2 }}
      {...other}
    >
      {children}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

function CustomApportionmentModal({
  open, setOpen, apportionmentData, mappedPonds, callback = () => {},
}) {
  const [remainingTotalDisplayError, setRemainingTotalDisplayError] = useState('');

  // eslint-disable-next-line func-names
  Yup.addMethod(Yup.number, 'customCast', function () {
    // eslint-disable-next-line react/no-this-in-sfc
    return this.transform((value, originalValue) => {
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(originalValue)) {
        return null; // ou qualquer valor padrão desejado
      }
      return value;
    });
  });

  const formik = useFormik({
    initialValues: {
      pondsModal: [
        {
          pond: null,
          value: null,
        },
      ],
    },
    validationSchema: Yup.object({
      pondsModal: Yup.array(Yup.object({
        pond: Yup.object().required('Campo Obrigatório').nullable(),
        value: Yup.number().required('Campo Obrigatório').nullable().customCast(),
      })),
    }),
    onSubmit: (values) => {
      // eslint-disable-next-line no-use-before-define
      if (remainingTotal === 0 && formik.isValid) {
        callback(values.pondsModal);
        setOpen(false);
      } else {
        setRemainingTotalDisplayError('A soma dos valores deve ser igual ao valor total.');
      }
    },
  });

  const hasPonds = formik.values.pondsModal && formik.values.pondsModal.length > 0;

  const handleClose = () => {
    setOpen(false);
  };

  const availablePondCycles = () => {
    const pondInUse = formik.values.pondsModal.map((item) => item.pond?.id);

    const pondFiltered = mappedPonds.filter((pondCycle) => !pondInUse.includes(pondCycle.pond?.id));

    return pondFiltered;
  };

  // recupera as informações do campo viveiros se houver
  useEffect(() => {
    if (apportionmentData.type === 'Personalizado' && apportionmentData.ponds?.length > 0) {
      formik.setValues({
        pondsModal: [...apportionmentData.ponds],
      });
    }
  }, [apportionmentData.ponds, apportionmentData.type]);

  const remainingTotal = useMemo(() => {
    const totalValue = formik.values.pondsModal.reduce((acc, curr) => acc + curr.value || 0, 0);
    return apportionmentData.value - totalValue;
  }, [apportionmentData.value, formik.values.pondsModal]);

  // limpar erro quando a quantidade restante for igual a 0.
  useEffect(() => {
    if (remainingTotal === 0) {
      setRemainingTotalDisplayError('');
    }
  }, [remainingTotal]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      fullWidth
      maxWidth="md"
    >
      <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
        <Header>
          <Box>
            <Typography fontSize={22}>Rateio por viveiro</Typography>
            <Typography fontSize={14}>Rateio personalizado</Typography>
          </Box>
          <Box textAlign="end">
            <Typography fontSize={14}>{`${apportionmentData.accountCenter?.cod} ${apportionmentData.accountCenter?.description}`}</Typography>
            <Typography fontSize={14}>{`Valor total: ${formatValueInBrazilianCurrency(apportionmentData.value)}`}</Typography>
            {remainingTotal > 0 && (
              <Typography fontSize={14}>
                {'Total restante: '}
                <span style={{ color: 'red' }}>{formatValueInBrazilianCurrency(remainingTotal)}</span>
              </Typography>
            )}
          </Box>
        </Header>
      </BootstrapDialogTitle>
      <DialogContent sx={{ maxHeight: 300, mt: 1 }}>
        {remainingTotalDisplayError && (
          <Typography mb={2} mt={-1} color="red" width="100%">{remainingTotalDisplayError}</Typography>
        )}
        <form onSubmit={formik.handleSubmit}>
          <FormikProvider value={formik}>
            <FieldArray name="pondsModal">
              {({ remove, unshift }) => (
                <Box display="flex" flexDirection="column" gap={2}>
                  {hasPonds && (
                    formik.values.pondsModal.map((_, index) => (
                      <InputGroup
                        key={index}
                        ponds={availablePondCycles()}
                        index={index}
                        totalApportionmentValue={apportionmentData.value}
                        onRemove={() => remove(index)}
                        onAdd={() => unshift({
                          pond: null,
                          value: null,
                        })}
                      />
                    ))
                  )}
                </Box>
              )}
            </FieldArray>
          </FormikProvider>
          <DialogActions sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '2.5rem',
            padding: 0,
          }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setOpen(false)}
              // disabled={formik.isSubmitting}
              style={{
                border: '1px solid red',
                color: 'red',
                borderRadius: '10px',
                padding: 8,
              }}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              type="submit"
              // disabled={formik.isSubmitting}
              style={{
                backgroundColor: '#0267C1',
                color: 'white',
                borderRadius: '10px',
                padding: 8,
              }}
            >
              Adicionar
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </BootstrapDialog>
  );
}

export default CustomApportionmentModal;
